import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

/* Scrolling has to travel this far before the bar reacts, so small trackpad
   jitter no longer makes it flicker in and out. */
const HIDE_AFTER = 120;
const SCROLL_DELTA = 8;

/* Routes whose first screen is a dark banner or hero. Only these let the
   navbar sit transparent over the page at scroll-top — anywhere else the
   white nav text would land on a white background. */
const OVERLAY_ROUTES = [
  '/',
  '/lab-website-v2',
  '/projects',
  '/team',
  '/news',
  '/join',
  '/ongoing-projects-list',
  '/recently-completed-projects',
  // Detail pages that open with a dark hero. GeoHealth is deliberately not
  // here: its header is light, so the bar has to stay solid.
  '/audt',
  '/hurricane',
  '/hurricanedebby',
  '/dt-book',
  '/internship',
];

const OVERLAY_PREFIXES = ['/projects/', '/content/', '/parisolympic', '/news/all'];

const isOverlayRoute = (pathname) => {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return OVERLAY_ROUTES.includes(path) || OVERLAY_PREFIXES.some((p) => path.startsWith(p));
};

function Navbar({ navLinks }) {
  const [click, setClick] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [isOpaque, setIsOpaque] = useState(false);

  const location = useLocation();

  const handleClick = () => setClick((open) => !open);
  const closeMobileMenu = useCallback(() => setClick(false), []);

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Lock body scroll behind the open drawer
  useEffect(() => {
    document.body.style.overflow = click ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [click]);

  // Close the drawer on Escape
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMobileMenu]);

  useEffect(() => {
    let prevScrollPos = window.pageYOffset;
    let ticking = false;

    const update = () => {
      const currentScrollPos = window.pageYOffset;
      const diff = currentScrollPos - prevScrollPos;

      setIsOpaque(currentScrollPos > 12);

      if (Math.abs(diff) > SCROLL_DELTA) {
        // Only retreat once the header is well clear of the viewport top
        setHidden(diff > 0 && currentScrollPos > HIDE_AFTER);
        prevScrollPos = currentScrollPos;
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isExternal = (path) => /^https?:\/\//i.test(path);

  const isActive = (path) =>
    !isExternal(path) &&
    (location.pathname.toLowerCase() === path.toLowerCase() ||
      (path !== '/' && location.pathname.toLowerCase().startsWith(`${path.toLowerCase()}/`)));

  return (
    <nav
      className={`navbar ${hidden && !click ? 'hidden' : ''} ${isOpaque ? 'opaque' : ''} ${
        isOverlayRoute(location.pathname) ? 'navbar--overlay' : ''
      }`}
      aria-label="Primary"
    >
      <div className="navbar-container">
        <div className="logo-container">
          <Link to="/" className="navbar-logo2" onClick={closeMobileMenu} aria-label="TExUS Lab — home">
            <img src={process.env.PUBLIC_URL + '/images/newLogo.svg'} width="70px" alt="Logo" className="logo-image2" />
          </Link>
          <span className="logo-divider" aria-hidden="true" />
          <a
            href="https://www.jsg.utexas.edu/"
            target="_blank"
            rel="noopener noreferrer"
            className="jsg-logo-link"
          >
            <img
              src={process.env.PUBLIC_URL + '/images/jsg_white_digital.png'}
              width="120px"
              alt="JSG Logo"
              className="jsg-logo"
            />
          </a>
        </div>

        <button
          type="button"
          className="menu-icon"
          onClick={handleClick}
          aria-expanded={click}
          aria-label={click ? 'Close menu' : 'Open menu'}
        >
          <i className={click ? 'fas fa-times' : 'fas fa-bars'} />
        </button>

        <ul className={click ? 'nav-menu active' : 'nav-menu'}>
          {navLinks.map((navLink, index) => (
            <li className="nav-item" key={index}>
              {isExternal(navLink.path) ? (
                /* Off-site entries (Publications goes to Google Scholar) open
                   in a new tab rather than replacing the site. A router Link
                   is the wrong element for these — it is built for internal
                   routes and cannot target a new tab. */
                <a
                  href={navLink.path}
                  className="nav-links"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                >
                  {navLink.label}
                  <span className="nav-links__ext" aria-hidden="true">↗</span>
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : (
                <Link
                  to={navLink.path}
                  className={`nav-links ${isActive(navLink.path) ? 'is-active' : ''}`}
                  onClick={closeMobileMenu}
                  aria-current={isActive(navLink.path) ? 'page' : undefined}
                >
                  {navLink.label}
                </Link>
              )}
              {navLink.sublinks.length > 0 && (
                <ul className="dropdown-content">
                  {navLink.sublinks.map((sublink, subIndex) => (
                    <li className="nav-item" key={subIndex}>
                      <Link
                        to={sublink.path}
                        className="nav-links"
                        onClick={closeMobileMenu}
                      >
                        {sublink.label}
                      </Link>

                      {/* Check for nested sublinks */}
                      {sublink.innerSublinks && sublink.innerSublinks.length > 0 && (
                        <ul className="inner-dropdown-content">
                          {sublink.innerSublinks.map((innerSublink, innerIndex) => (
                            <li className="nav-item" key={innerIndex}>
                              <Link
                                to={innerSublink.path}
                                className="nav-links"
                                onClick={closeMobileMenu}
                              >
                                {innerSublink.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li className="nav-item nav-item--cta">
            <Link to="/join" onClick={closeMobileMenu} className="joinus_link">
              <span className="joinus_button">Join Us</span>
            </Link>
          </li>
        </ul>

        <Link to="/join" onClick={closeMobileMenu} className="joinus_link joinus_link--desktop">
          <span className="joinus_button">Join Us</span>
        </Link>
      </div>

      <div
        className={`nav-scrim ${click ? 'is-visible' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />
    </nav>
  );
}

export default Navbar;
